import { i18n } from '../i18n.js';
import { Firebase } from '../firebase-loader.js';

/**
 * THE ACCOUNT'S SUMMONERS, WHOLE, READ ONCE (docs/asset-loading.md law 3,
 * "Small sets of the player's own are read whole, ahead").
 *
 * The owner, 28 September 2026: "statistically players won't have a lot of
 * Dex and they won't have a lot of created summoners ... you could load like
 * at least 20 ... by default they have only seven so they would have to
 * spend a fortune just to have more than 20".
 *
 * The summoner select (deck-selector.js) and the avatar picker
 * (avatar-picker.js) are two windows onto one list — the
 * `/api/deck-builder/characters-page` route: the public summoners first, then
 * the account's own — and each used to read it for itself, the selector four
 * rows at a time. This reads it ONCE per account and language, up to the
 * route's own cap for one read (ROSTER_LIMIT rows, which covers every account
 * that has not bought its way past 24 summoners), and both surfaces page it
 * locally. An account with more pages past it through the route's cursor
 * (`nextCursor`), each surface one page ahead, as before.
 *
 * Kept for the page session, never presented to another account or in
 * another language. A summoner made, confirmed or deleted on this device
 * drops it (Moon Dream announces it: `moonfall:summoners-changed`, and
 * `moonfall:holdings-changed` from Moon Dream when a run finishes or is
 * deleted). One kept longer than FRESH_MS is still presented, and re-read
 * behind the view (`revalidate`): a re-read that finds another list replaces
 * it and tells the surfaces showing it (`onChange`), so a change made on
 * another device reaches them too.
 */

let SummonerRoster = {};

SummonerRoster = (function () {
    /* The route's own cap for one read (server.js characters-page, max 24). */
    const ROSTER_LIMIT = 24;
    /* Presented without a re-read this long after it was read. */
    const FRESH_MS = 60_000;
    /** @typedef {{ characterId?: string, deckKey?: string, picture?: string, imageUrl?: string | null, name?: string, title?: string, bio?: string, communicationStyle?: string }} RosterRow */
    /** @typedef {{ key: string, rows: RosterRow[], nextCursor: string | null, isDone: boolean, at: number, settled: boolean, failed: boolean, promise: Promise<any> }} RosterEntry */
    /** @type {RosterEntry | null} */
    let current = null;
    /** @type {Promise<{ changed: boolean }> | null} */
    let revalidating = null;
    /** @type {Set<(next: RosterEntry | null, previous: RosterEntry | null) => void>} */
    const listeners = new Set();
    let generation = 0;

    function key() {
        let uid = '';
        try { uid = String(Firebase?.getUser?.()?.uid || ''); } catch (_) { uid = ''; }
        return `${uid}|${i18n?.getLanguage?.() || 'en'}`;
    }

    async function authHeaders() {
        if (!Firebase?.getIdToken) return {};
        try {
            const token = await Firebase.getIdToken();
            return token ? { Authorization: `Bearer ${token}` } : {};
        } catch (_) {
            return {};
        }
    }

    /**
     * One route read: the rows as the route sends them, its cursor, and
     * whether it is done. Throws when the route does not answer.
     * @param {string | null} cursor
     * @param {{ limit?: number, signal?: AbortSignal | null }} [options]
     */
    async function fetchRows(cursor = null, { limit = ROSTER_LIMIT, signal = null } = {}) {
        const headers = await authHeaders();
        if (signal?.aborted) {
            const error = new Error('Summoner read was cancelled');
            error.name = 'AbortError';
            throw error;
        }
        const params = new URLSearchParams({ limit: String(limit) });
        params.set('lang', i18n?.getLanguage?.() || 'en');
        if (cursor) params.set('cursor', cursor);
        const response = await fetch(`/api/deck-builder/characters-page?${params.toString()}`, {
            headers,
            ...(signal ? { signal } : {}),
        });
        if (!response.ok) throw new Error('Failed to fetch characters');
        const data = await response.json();
        const rows = Array.isArray(data?.items) ? data.items : [];
        const nextCursor = typeof data?.nextCursor === 'string' && data.nextCursor ? data.nextCursor : null;
        return { rows, nextCursor, isDone: Boolean(data?.isDone) || !nextCursor };
    }

    /** What a surface draws from a row: a re-read that changes none of it changed nothing. */
    function signatureOf(answer) {
        return JSON.stringify([
            (answer?.rows || []).map((row) => [
                row?.characterId ?? null,
                row?.deckKey ?? null,
                row?.picture ?? null,
                row?.imageUrl ?? null,
                row?.name ?? null,
                row?.title ?? null,
                row?.bio ?? null,
            ]),
            answer?.nextCursor ?? null,
            Boolean(answer?.isDone),
        ]);
    }

    function startRead(readKey, signal) {
        /** @type {RosterEntry} */
        const entry = {
            key: readKey,
            rows: [],
            nextCursor: null,
            isDone: false,
            at: 0,
            settled: false,
            failed: false,
            promise: Promise.resolve(null),
        };
        entry.promise = fetchRows(null, { signal }).then((answer) => {
            entry.rows = answer.rows;
            entry.nextCursor = answer.nextCursor;
            entry.isDone = answer.isDone;
            entry.at = Date.now();
            entry.settled = true;
            return entry;
        }, (error) => {
            entry.settled = true;
            entry.failed = true;
            if (current === entry) current = null;
            throw error;
        });
        // Every caller that awaits it reports its own failure.
        entry.promise.catch(() => {});
        return entry;
    }

    /**
     * The roster for this account and language: the one kept, whatever its
     * age (`revalidate` refreshes it), or a read started now. Never rejects
     * silently: a failed read rejects its promise and is forgotten, so the
     * next caller reads again.
     *
     * A read started with a `signal` belongs to whoever passed it (a dialog's
     * session): if that signal aborts before the read answers, the read is
     * dropped and forgotten, so a newer open never waits on an abandoned
     * request. A read started without one (the lobby's warm) belongs to
     * nobody and completes; a caller with a signal joins it as it is.
     * @param {{ signal?: AbortSignal | null }} [options]
     * @returns {RosterEntry}
     */
    function read({ signal = null } = {}) {
        const readKey = key();
        if (current && current.key === readKey && !current.failed) return current;
        generation += 1;
        const entry = startRead(readKey, signal);
        current = entry;
        signal?.addEventListener('abort', () => {
            if (current === entry && !entry.settled) {
                generation += 1;
                current = null;
            }
        }, { once: true });
        return entry;
    }

    /** The answered roster for this account and language, or null. */
    function peek() {
        const entry = current;
        return entry && entry.settled && !entry.failed && entry.key === key() ? entry : null;
    }

    function isFresh(entry) {
        return Boolean(entry?.settled && !entry.failed && Date.now() - entry.at < FRESH_MS);
    }

    function notify(next, previous) {
        listeners.forEach((listener) => {
            try {
                listener(next, previous);
            } catch (error) {
                console.warn('[SummonerRoster] A listener failed:', error);
            }
        });
    }

    /**
     * Read the roster again behind the view when the kept one is older than
     * FRESH_MS (or always, with `force`). The kept one stays presented until
     * the answer lands; an answer that changes nothing only renews its age,
     * one that does replaces it and tells the listeners. Resolves
     * `{ changed }`; never rejects.
     * @param {{ force?: boolean }} [options]
     */
    function revalidate({ force = false } = {}) {
        const entry = peek();
        if (!entry) return Promise.resolve({ changed: false });
        if (!force && isFresh(entry)) return Promise.resolve({ changed: false });
        if (revalidating) return revalidating;
        const readGeneration = generation;
        const task = fetchRows(null).then((answer) => {
            // Dropped, re-read or signed out meanwhile: this answer is nobody's.
            if (current !== entry || generation !== readGeneration || entry.key !== key()) return { changed: false };
            if (signatureOf(answer) === signatureOf(entry)) {
                entry.at = Date.now();
                return { changed: false };
            }
            generation += 1;
            /** @type {RosterEntry} */
            const next = {
                key: entry.key,
                rows: answer.rows,
                nextCursor: answer.nextCursor,
                isDone: answer.isDone,
                at: Date.now(),
                settled: true,
                failed: false,
                promise: Promise.resolve(null),
            };
            next.promise = Promise.resolve(next);
            current = next;
            notify(next, entry);
            return { changed: true };
        }, () => ({ changed: false })).finally(() => {
            if (revalidating === task) revalidating = null;
        });
        revalidating = task;
        return task;
    }

    /** Forget the kept roster: the next read asks the route again. */
    function invalidate() {
        const previous = current;
        generation += 1;
        current = null;
        revalidating = null;
        if (previous) notify(null, previous);
    }

    /** @param {(next: RosterEntry | null, previous: RosterEntry | null) => void} listener */
    function onChange(listener) {
        if (typeof listener !== 'function') return () => {};
        listeners.add(listener);
        return () => listeners.delete(listener);
    }

    /* A summoner made, confirmed or deleted on this device. */
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        window.addEventListener('moonfall:summoners-changed', () => invalidate());
        window.addEventListener('moonfall:holdings-changed', (event) => {
            const detail = /** @type {CustomEvent} */ (event)?.detail;
            if (detail?.source === 'moon-dream') invalidate();
        });
    }

    return {
        ROSTER_LIMIT,
        FRESH_MS,
        read,
        peek,
        isFresh,
        revalidate,
        invalidate,
        onChange,
        fetchRows,
        key,
    };
})();

export { SummonerRoster };
